import * as React from "react";
import PropTypes from "prop-types";
import Header from "./Header";
import { makeStyles } from "@fluentui/react-components";
import { Button } from "@fluentui/react-components"; // Button for TOC and TOA actions
import TOATOC from "./TOATOC";
import GeneratePrompt from "./GeneratePrompt";
import UploadDocument from "./UploadDocument";
import oplogo from '../../../assets/mensrea-logo.png';
import Tocnewjava from "./Tocnewjava";
const useStyles = makeStyles({
  root: {
    minHeight: "100vh",
    padding: "20px",
  },
  toaContainer: {
    marginTop: "20px",
    border: "1px solid #ddd",
    padding: "20px",
    backgroundColor: "#f9f9f9",
    borderRadius: "8px",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    marginTop: "20px",
  },
  tableHeader: {
    backgroundColor: "#4CAF50",
    color: "white",
    textAlign: "left",
    padding: "8px",
  },
  tableCell: {
    border: "1px solid #ddd",
    padding: "8px",
    textAlign: "left",
  },
  tocContainer: {
    marginTop: "20px",
    padding: "10px",
    backgroundColor: "#f2f2f2",
    border: "1px solid #ddd",
    borderRadius: "8px",
  },
  tocHeader: {
    fontSize: "20px",
    fontWeight: "bold",
    marginBottom: "10px",
  },
  tocList: {
    listStyleType: "none",
    paddingLeft: "0",
  },
  tocItem: {
    marginBottom: "5px",
  },
  
});

const App = (props) => {
  const { title } = props;
  const styles = useStyles();

  // Sample authorities (could be legal cases, statutes, etc.)
  const sampleAuthorities1 = [
    { name: "Smith v. Jones", reference: "123 F.3d 456" },
    { name: "Doe v. United States", reference: "789 U.S. 101" },
    { name: "Miller v. California", reference: "123 S.Ct. 456" },
    { name: "Johnson v. New York", reference: "456 N.Y. 789" },
  ];

  const sampleAuthorities2 = [
    { name: "Anderson v. Texas", reference: "124 U.S. 101" },
    { name: "Roberts v. California", reference: "567 F.2d 789" },
    { name: "Bates v. Michigan", reference: "321 U.S. 456" },
    { name: "Wilson v. New Jersey", reference: "222 N.J. 333" },
  ];

  const sampleAuthorities3 = [
    { name: "Adams v. Virginia", reference: "111 S.Ct. 222" },
    { name: "Black v. Georgia", reference: "789 U.S. 900" },
    { name: "Clark v. Washington", reference: "654 F.3d 987" },
    { name: "Green v. Florida", reference: "123 F.3d 456" },
  ];

  // Function to generate TOA from marked citations
  const generateTOA = async () => {
    try {
      await Word.run(async (context) => {
        const body = context.document.body;
        const contentControls = body.contentControls;
        contentControls.load("items");

        await context.sync();

        // Collect TOA citations and insert TOA at end of document
        const citations = contentControls.items
          .filter(control => control.tag === "TOA_Citation")
          .map((control, index) => `Citation ${index + 1}: ${control.text}`);

        // Add TOA header and citations list at the end
        body.insertParagraph("Table of Authorities", Word.InsertLocation.end).styleBuiltIn = Word.Style.heading1;

        // Insert sample authorities for TOA 1 (Sample Authorities 1)
        sampleAuthorities1.forEach((authority, index) => {
          body.insertParagraph(`Authority ${index + 1}: ${authority.name}, ${authority.reference}`, Word.InsertLocation.end);
        });

        // Insert sample authorities for TOA 2 (Sample Authorities 2)
        body.insertParagraph("Table of Authorities 2", Word.InsertLocation.end).styleBuiltIn = Word.Style.heading1;
        sampleAuthorities2.forEach((authority, index) => {
          body.insertParagraph(`Authority ${index + 1}: ${authority.name}, ${authority.reference}`, Word.InsertLocation.end);
        });

        // Insert sample authorities for TOA 3 (Sample Authorities 3)
        body.insertParagraph("Table of Authorities 3", Word.InsertLocation.end).styleBuiltIn = Word.Style.heading1;
        sampleAuthorities3.forEach((authority, index) => {
          body.insertParagraph(`Authority ${index + 1}: ${authority.name}, ${authority.reference}`, Word.InsertLocation.end);
        });

        // If there are marked citations, insert them as well
        citations.forEach(citation => {
          body.insertParagraph(citation, Word.InsertLocation.end);
        });

        await context.sync();
        console.log("TOA generated successfully.");
      });
    } catch (error) {
      console.error("Error generating TOA:", error);
    }
  };

  // Function to generate TOC (Table of Contents) for the page
  const generateTOC = () => {
    return (
      <div className={styles.tocContainer}>
        <div className={styles.tocHeader}>Table of Contents</div>
        <ul className={styles.tocList}>
          <li className={styles.tocItem}>
            <a href="#toa1" aria-label="Link to Table of Authorities 1">Table of Authorities 1</a>
          </li>
          <li className={styles.tocItem}>
            <a href="#toa2" aria-label="Link to Table of Authorities 2">Table of Authorities 2</a>
          </li>
          <li className={styles.tocItem}>
            <a href="#toa3" aria-label="Link to Table of Authorities 3">Table of Authorities 3</a>
          </li>
        </ul>
      </div>
    );
  };

  const generateWordTOC = async () => {
    try {
      await Word.run(async (context) => {
        const body = context.document.body;
        
        // Insert an introduction (optional)
        body.insertParagraph("This document outlines the sections covered below.", Word.InsertLocation.start);
        
        // Insert Table of Contents heading (Heading 1 style)
        body.insertParagraph("Table of Contents", Word.InsertLocation.end).styleBuiltIn = Word.Style.heading1;
  
        // Insert sections with Heading 2 style (which will be used in the TOC)
        body.insertParagraph("1. Introduction", Word.InsertLocation.end).styleBuiltIn = Word.Style.heading2;
        body.insertParagraph("2. Overview of Legal Authorities", Word.InsertLocation.end).styleBuiltIn = Word.Style.heading2;
        body.insertParagraph("3. Detailed Analysis", Word.InsertLocation.end).styleBuiltIn = Word.Style.heading2;
  
        // Insert the actual TOC (using Word's TOC field)
        const tocField = body.insertParagraph("", Word.InsertLocation.end);
        tocField.insertText("Table of Contents: (this will be updated automatically)", Word.InsertLocation.start);
  
        // Synchronize changes with the document
        await context.sync();
        
        console.log("TOC inserted in Word document.");
      });
    } catch (error) {
      console.error("Error inserting TOC in Word document:", error);
    }
  };
  
  
  
  
  
  

  return (
    <div className={styles.root}>
      <Header logo={oplogo} title={title} />

      {/* Add buttons for TOC, mark citation for TOA, and generate TOA */}
      {/* <div style={{ margin: "20px 0" }}>
        <Button appearance="primary" style={{ marginLeft: "10px",marginBottom:"1px" }} onClick={generateTOA}>
          Generate Table of Authorities
        </Button>
        <Button appearance="primary" style={{ marginLeft: "10px" }} onClick={generateWordTOC}>
          Generate Table of Contents (Word)
        </Button>
      </div> */}
      {/* <TOATOC/> */}
      {<GeneratePrompt/>}
      {/* <UploadDocument/> */}
      {<Tocnewjava/>}

      {/* Table of Contents for the webpage */}
      {/* {generateTOC()} */}

      {/* Table of Authorities HTML Page */}
      {/* <div className={styles.toaContainer} id="toa1">
        <h2>Table of Authorities 1</h2>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.tableHeader}>Authority</th>
              <th className={styles.tableHeader}>Reference</th>
            </tr>
          </thead>
          <tbody>
            {sampleAuthorities1.map((authority, index) => (
              <tr key={index}>
                <td className={styles.tableCell}>{authority.name}</td>
                <td className={styles.tableCell}>{authority.reference}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className={styles.toaContainer} id="toa2">
        <h2>Table of Authorities 2</h2>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.tableHeader}>Authority</th>
              <th className={styles.tableHeader}>Reference</th>
            </tr>
          </thead>
          <tbody>
            {sampleAuthorities2.map((authority, index) => (
              <tr key={index}>
                <td className={styles.tableCell}>{authority.name}</td>
                <td className={styles.tableCell}>{authority.reference}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className={styles.toaContainer} id="toa3">
        <h2>Table of Authorities 3</h2>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.tableHeader}>Authority</th>
              <th className={styles.tableHeader}>Reference</th>
            </tr>
          </thead>
          <tbody>
            {sampleAuthorities3.map((authority, index) => (
              <tr key={index}>
                <td className={styles.tableCell}>{authority.name}</td>
                <td className={styles.tableCell}>{authority.reference}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div> */}
    </div>
  );
};

App.propTypes = {
  title: PropTypes.string,
};

export default App;
